import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Download, Package, Users, Contact, CalendarClock, Tag, Award, ListChecks, FileJson, FileSpreadsheet, Info,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ENTITIES = [
  { key: "clients",           label: "Clienti",              icon: Contact,       hint: "Anagrafica completa (nome, cognome, email, telefono, note)." },
  { key: "appointments",      label: "Appuntamenti",         icon: CalendarClock, hint: "Tutti gli appuntamenti con nomi denormalizzati (consulente, cliente, tipo, stato, esito)." },
  { key: "users",             label: "Collaboratori",        icon: Users,         hint: "Utenti del sistema (email, ruolo, colore). NESSUNA password inclusa." },
  { key: "appointment_types", label: "Tipi Appuntamento",    icon: Tag,           hint: "Catalogo con durata, prezzo, colore e calendario di appartenenza." },
  { key: "outcome_types",     label: "Stati (Statuses)",     icon: ListChecks,    hint: "5 stati unificati con is_completed flag." },
  { key: "result_types",      label: "Esiti (Results)",      icon: Award,         hint: "Esiti per calendario Consulenti / Trainers." },
];

function download(url, headers = {}) {
  return fetch(url, { credentials: "include", headers }).then(async (res) => {
    if (!res.ok) throw new Error(`Export fallito (${res.status})`);
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : "export";
    const blob = await res.blob();
    const link = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = link;
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(link);
    return filename;
  });
}

export default function ExportPage() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(null);

  const allowed = user && ["admin", "manager", "receptionist"].includes(user.role);

  useEffect(() => {
    if (!allowed) return;
    Promise.all([
      api.get("/clients"),
      api.get("/appointments"),
      api.get("/users"),
      api.get("/appointment-types"),
      api.get("/outcome-types"),
      api.get("/result-types"),
    ]).then(([c, a, u, at, ot, rt]) => {
      setCounts({
        clients: c.data.length,
        appointments: a.data.length,
        users: u.data.length,
        appointment_types: at.data.length,
        outcome_types: ot.data.length,
        result_types: rt.data.length,
      });
    }).catch(() => {});
  }, [allowed]);

  if (user && !allowed) return <Navigate to="/" replace />;

  const runDownload = async (label, url) => {
    setBusy(label);
    try {
      const filename = await download(url);
      toast.success(`Scaricato: ${filename}`);
    } catch (e) {
      toast.error(e.message || "Export fallito");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="max-w-5xl">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold tracking-tight">Esportazione Dati</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Scarica in qualsiasi momento un backup completo dei dati inseriti. Formati compatibili con Excel/Google Sheets e con la maggior parte dei CRM (import via CSV o JSON).
          </p>
        </div>

        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardContent className="p-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="h-11 w-11 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg">Backup completo</div>
                <p className="text-sm text-muted-foreground">
                  Un unico archivio con tutte le collezioni. Ideale per migrare al futuro gestionale con CRM integrato.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    onClick={() => runDownload("all-zip", `${API}/export/all?format=zip`)}
                    disabled={busy === "all-zip"}
                    data-testid="export-all-zip-button"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {busy === "all-zip" ? "Preparazione…" : "Scarica ZIP (CSV + README)"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => runDownload("all-json", `${API}/export/all?format=json`)}
                    disabled={busy === "all-json"}
                    data-testid="export-all-json-button"
                  >
                    <FileJson className="h-4 w-4 mr-2" />
                    {busy === "all-json" ? "Preparazione…" : "Scarica JSON (backup fedele)"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-3 text-xs uppercase font-bold tracking-widest text-muted-foreground">Esportazioni singole</div>
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {ENTITIES.map((e) => {
            const Icon = e.icon;
            const n = counts?.[e.key];
            const busyC = busy === `${e.key}-csv`;
            const busyJ = busy === `${e.key}-json`;
            return (
              <Card key={e.key} data-testid={`export-card-${e.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-slate-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-display font-bold">{e.label}</div>
                        {typeof n === "number" && <Badge variant="secondary" data-testid={`export-count-${e.key}`}>{n} righe</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{e.hint}</p>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runDownload(`${e.key}-csv`, `${API}/export/${e.key}?format=csv`)}
                          disabled={busyC}
                          data-testid={`export-${e.key}-csv-button`}
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                          {busyC ? "…" : "CSV"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => runDownload(`${e.key}-json`, `${API}/export/${e.key}?format=json`)}
                          disabled={busyJ}
                          data-testid={`export-${e.key}-json-button`}
                        >
                          <FileJson className="h-3.5 w-3.5 mr-1.5" />
                          {busyJ ? "…" : "JSON"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 space-y-1">
              <div className="font-semibold text-sm">Note tecniche per la migrazione a CRM</div>
              <p>• Delimitatore CSV: <code className="bg-amber-100 px-1 rounded">;</code> (compatibile con Excel Italia). Encoding: UTF-8.</p>
              <p>• Le password degli utenti <strong>non</strong> vengono mai esportate.</p>
              <p>• Gli <code>id</code> sono ObjectId MongoDB in formato stringa esadecimale, utili come chiave di join.</p>
              <p>• In <code>appointments.csv</code> i campi <em>collaborator_name</em>, <em>client_first_name/last_name/email</em>, <em>appointment_type</em>, <em>status_label</em>, <em>result_label</em> sono denormalizzati per un import immediato senza join.</p>
              <p>• Lo ZIP include un file <code>README.txt</code> con lo schema di join.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
